import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : '');

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [passwordConfigured, setPasswordConfigured] = useState(true);

  // Check auth status on mount
  const checkAuthStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.get(`${API_BASE}/api/auth/status`, { headers });
      const data = response.data;

      setIsAuthenticated(data.authenticated);
      setPasswordConfigured(data.password_configured);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = async (password) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/login`, { password });
      const { token } = response.data;

      localStorage.setItem('auth_token', token);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('auth_token');
    setIsAuthenticated(false);
  };

  const setupPassword = async (password) => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/setup`, { password });
      const { token } = response.data;

      localStorage.setItem('auth_token', token);
      setIsAuthenticated(true);
      setPasswordConfigured(true);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Setup failed';
      return { success: false, error: message };
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    passwordConfigured,
    login,
    logout,
    setupPassword,
    checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
